// src/components/pages/dialogs/CancellationReasonDialog.tsx

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cancelarCita } from '@/components/hooks/useCitas';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { X } from 'lucide-react';

export type CancellationReasonDialogProps = {
  idCita: string;
  motivo: string;
  setMotivo: (value: string) => void;
  open: boolean;
  onCancel: () => void;
  onCloseAll: () => void;
  incrementReload: () => void;
};

const CancellationReasonDialog = ({
  idCita,
  motivo,
  setMotivo,
  open,
  onCancel,
  onCloseAll,
  incrementReload,
}: CancellationReasonDialogProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);

    const resp = await cancelarCita(idCita, motivo);

    setLoading(false);

    if (resp.ok) {
      toast.success(t('cancellationReasonDialog.successToast'));
      onCloseAll();
      incrementReload();
    } else {
      toast.error(t('cancellationReasonDialog.errorToast'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-red-600 dark:text-red-400 pr-10">
              {t('cancellationReasonDialog.title', 'Cancelar cita')}
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 pb-5">
          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-3 space-y-2 shadow-sm">
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              {t('cancellationReasonDialog.placeholder')}
            </label>
            <Textarea
              rows={4}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t('cancellationReasonDialog.placeholder')}
              className="resize-none rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pb-5 flex gap-3 flex-col sm:flex-row shrink-0">
          <Button
            onClick={onCancel}
            disabled={loading}
            variant="outline"
            className="rounded-lg font-medium w-full bg-transparent dark:bg-transparent border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          >
            {t('cancellationReasonDialog.back')}
          </Button>

          <Button
            onClick={handleConfirm}
            disabled={loading}
            className="
              rounded-xl text-white font-semibold text-base shadow-md py-3
              bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700
              disabled:opacity-50 w-full transition-colors
            "
          >
            {loading
              ? t('cancellationReasonDialog.confirming')
              : t('cancellationReasonDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancellationReasonDialog;